import { App, Form, Select } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UNIT_ICON, VEHICLE_UNIT_ICON } from "src/assets/svgs";
import { CustomButton, CustomSelect, CustomTypography } from "sipli-fleet-core/components";
import UnitInfoGreyCard from "../components/UnitInfoGreyCard";
import UnitManagementPanel from "../components/UnitManagementPanel";
import {
  extractLookupItems,
  extractVehicleSearchItems,
  getDeviceSerialFromVehicle,
  getQueryData,
  getSerialFromLookupItem,
  MIN_SEARCH_LENGTH,
  toOptions,
  toUnitOptions,
  toVehicleOptions,
} from "../shared/unitManagementUtils";
import {
  useMutationChangeUnit,
  useQueryGetUnitManagementAdjusterLookup,
  useQueryGetUnitManagementClientLookup,
  useQueryGetVehicleById,
  useQuerySearchUnitByImeiOrSerial,
  useQuerySearchVehicle,
} from "src/queries/unitManagement.queries";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import "../Install/Install.css";
import "./Change.css";

interface ChangeFormValues {
  adjusterId?: string | number;
  customerId?: string | number;
  vehicleId?: string | number;
  newDeviceId?: string | number;
}

const VehicleUnitIcon = () => (
  <img src={VEHICLE_UNIT_ICON} alt="" className="h-5 w-5" />
);

const UnitIcon = () => <img src={UNIT_ICON} alt="" className="h-5 w-5" />;

const Change = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<ChangeFormValues>();
  const selectedCustomerId = Form.useWatch("customerId", form);
  const selectedVehicleId = Form.useWatch("vehicleId", form);
  const selectedNewDeviceId = Form.useWatch("newDeviceId", form);
  const { selectedDistributor } = useSelectDistributor();
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const distributorId = selectedDistributor ?? undefined;

  const { data: adjusterData, isLoading: isAdjusterLoading } =
    useQueryGetUnitManagementAdjusterLookup(distributorId ? { distributorId } : {}, {
      enabled: !!distributorId,
    });
  const { data: clientData, isLoading: isClientLoading } = useQueryGetUnitManagementClientLookup(
    distributorId ? { distributorId } : {},
    { enabled: !!distributorId }
  );
  const { data: vehicleSearchData, isLoading: isVehicleSearchLoading } = useQuerySearchVehicle(
    selectedCustomerId ? { clientId: selectedCustomerId, query: vehicleSearch } : undefined,
    { enabled: !!selectedCustomerId }
  );
  const { data: vehicleDetail, isLoading: isVehicleDetailLoading } = useQueryGetVehicleById(
    selectedVehicleId,
    { enabled: !!selectedVehicleId }
  );
  const { data: deviceSearchData, isLoading: isDeviceSearchLoading } =
    useQuerySearchUnitByImeiOrSerial({
      query: deviceSearch,
      unitType: "DVC",
      distributorId,
    });

  const { mutateAsync: changeUnit, isPending: isChanging } = useMutationChangeUnit();

  const adjusterOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(adjusterData))),
    [adjusterData]
  );
  const clientOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(clientData))),
    [clientData]
  );
  const vehicleOptions = useMemo(
    () => toVehicleOptions(extractVehicleSearchItems(getQueryData(vehicleSearchData)), t),
    [vehicleSearchData, t]
  );
  const deviceItems = useMemo(
    () => extractLookupItems(getQueryData(deviceSearchData)),
    [deviceSearchData]
  );
  const deviceOptions = useMemo(() => toUnitOptions(deviceItems), [deviceItems]);

  const selectedDevice = useMemo(
    () => deviceItems.find((item) => (item.id ?? item.value) === selectedNewDeviceId) ?? null,
    [deviceItems, selectedNewDeviceId]
  );

  const currentDeviceSerial = getDeviceSerialFromVehicle(vehicleDetail);
  const newDeviceSerial = getSerialFromLookupItem(selectedDevice);

  const handleCustomerChange = () => {
    form.setFieldValue("vehicleId", undefined);
    setVehicleSearch("");
  };

  const handleReset = () => {
    form.resetFields();
    setVehicleSearch("");
    setDeviceSearch("");
  };

  const handleFinish = async (values: ChangeFormValues) => {
    if (!vehicleDetail?.id || !selectedDevice?.id) return;

    await changeUnit({
      adjusterId: values.adjusterId as string | number,
      clientId: values.customerId as string | number,
      comments: "",
      vehicleId: vehicleDetail.id,
      newUnitId: selectedDevice.id as string | number,
    });

    message.success(t("unit_changed_successfully"));
    handleReset();
  };

  return (
    <div className="device-change-card">
      <CustomTypography.CustomTitle className="!mb-6 !text-[19.93px] !font-bold !text-black">
        {t("change")}
      </CustomTypography.CustomTitle>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="device-change-form"
        onFinish={handleFinish}
        onValuesChange={(changed) => {
          if ("customerId" in changed) handleCustomerChange();
        }}
      >
        <div className="grid gap-4 xl:grid-cols-2">
          <UnitManagementPanel
            icon={<VehicleUnitIcon />}
            title={t("vehicle")}
            description={t("change_vehicle_description")}
          >
            <div className="grid gap-x-2 md:grid-cols-2">
              <Form.Item
                name="adjusterId"
                label={`${t("adjuster")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={adjusterOptions}
                  loading={isAdjusterLoading}
                  disabled={!distributorId}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="customerId"
                label={`${t("customer")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={clientOptions}
                  loading={isClientLoading}
                  disabled={!distributorId}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </div>
            <Form.Item
              name="vehicleId"
              label={`${t("search_vehicle")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder={t("search_vehicle")}
                options={vehicleOptions}
                loading={isVehicleSearchLoading}
                disabled={!selectedCustomerId}
                className="!h-8"
                onSearch={setVehicleSearch}
                notFoundContent={
                  vehicleSearch.trim().length < MIN_SEARCH_LENGTH
                    ? t("type_at_least_3_characters")
                    : t("no_vehicle_found")
                }
              />
            </Form.Item>

            {selectedVehicleId ? (
              <UnitInfoGreyCard
                icon={<UnitIcon />}
                label={t("current_device_serial_number")}
                value={isVehicleDetailLoading ? "..." : currentDeviceSerial || t("no_data")}
              />
            ) : null}
          </UnitManagementPanel>

          <UnitManagementPanel
            icon={<UnitIcon />}
            title={t("new_device")}
            description={t("change_new_device_description")}
          >
            <Form.Item
              name="newDeviceId"
              label={`${t("search_new_device")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder={t("search_by_imei_or_serial")}
                options={deviceOptions}
                loading={isDeviceSearchLoading}
                disabled={!distributorId}
                className="!h-8"
                onSearch={setDeviceSearch}
                notFoundContent={
                  deviceSearch.trim().length < MIN_SEARCH_LENGTH
                    ? t("type_at_least_3_characters")
                    : t("no_data")
                }
              />
            </Form.Item>

            {selectedNewDeviceId ? (
              <UnitInfoGreyCard
                icon={<UnitIcon />}
                label={t("new_device_serial_number")}
                value={newDeviceSerial || t("no_data")}
              />
            ) : null}
          </UnitManagementPanel>
        </div>

        <div className="mt-8 flex justify-end gap-2">
          <CustomButton
            label={t("reset")}
            buttonSize="big"
            onClick={handleReset}
            disabled={isChanging}
          />
          <CustomButton
            type="primary"
            label={t("change_device")}
            buttonSize="big"
            htmlType="submit"
            loading={isChanging}
            className="!text-white"
          />
        </div>
      </Form>
    </div>
  );
};

export default Change;
